import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setFormField, setFullForm, resetForm } from "../../store/video";
import { useCreateVideoMutation } from "../../services/modules/video";
import { useGetAllNetworksQuery } from "../../services/modules/network";
import { useGetAllCategoriesQuery } from "../../services/modules/category";
import { toast } from "react-toastify";

import { extractVimeoId } from "./extractVideoInfo";

const VideoUploader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const form = useSelector((state) => state.video.form);

  const [createVideo, { isLoading }] = useCreateVideoMutation();
  const { data: categoriesData } = useGetAllCategoriesQuery();
  const { data: networksData } = useGetAllNetworksQuery();

  const categories = categoriesData?.data?.result || [];
  const networks = networksData?.data?.result || [];

  useEffect(() => {
    const vimeoEmbedUrl = extractVimeoId(form.url);
    if (!vimeoEmbedUrl) return; // non è un URL valido di Vimeo

    if (form.url !== vimeoEmbedUrl) {
      // Normalizziamo subito l'URL così che anche il submit usi quello pulito.
      dispatch(setFormField({ field: "url", value: vimeoEmbedUrl }));
      return;
    }

    const fetchVimeoMeta = async () => {
      try {
        const apiURL = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
          vimeoEmbedUrl
        )}`;
        const response = await fetch(apiURL);
        if (!response.ok) throw new Error("Invalid Vimeo response");

        const data = await response.json();

        dispatch(
          setFullForm({
            title: form.title || data.title,
            description: form.description || data.description,
            thumbnail: form.thumbnail || data.thumbnail_url,
          })
        );
      } catch (err) {
        console.error("Vimeo fetch failed:", err);
        toast.error("Unable to fetch Vimeo info. Please fill in manually.");
      }
    };

    fetchVimeoMeta();
  }, [form.url, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(setFormField({ field: name, value }));
  };

  const handleNetworksChange = (e) => {
    const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    dispatch(setFormField({ field: "networkId", value: values }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.categoryId || !form.networkId?.length) {
      toast.error("Please select both a category and at least one network.");
      return;
    }

    try {
      const payload = {
        title: form.title,
        description: form.description,
        url: form.url,
        thumbnail: form.thumbnail,
        categoryIds: [Number(form.categoryId)],
        networkIds: (form.networkId || []).map((id) => Number(id)), // multi
        channelId: 1,
      };

      await createVideo(payload).unwrap();
      toast.success("Video uploaded successfully!");
      dispatch(resetForm());
      setTimeout(() => navigate("/admin/videos"), 800);
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Failed to upload video. Please check your inputs.");
    }
  };

  const selectedCount = form.networkId?.length || 0;
  const totalNetworks = networks.length;

  return (
    <div className="max-w-xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6 text-center">Salva Video</h2>

      <form
        onSubmit={handleSubmit}
        className="bg-white p-8 rounded shadow-md space-y-6 w-full max-w-2xl"
      >
        <input
          type="text"
          name="title"
          placeholder="Titolo"
          value={form.title}
          onChange={handleChange}
          className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          required
        />

        <textarea
          name="description"
          placeholder="Descrizione"
          value={form.description}
          onChange={handleChange}
          className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          rows="4"
          required
        />

        <input
          type="url"
          name="url"
          placeholder="Video URL (es. Vimeo)"
          value={form.url}
          onChange={handleChange}
          className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          required
        />

        <input
          type="url"
          name="thumbnail"
          placeholder="Thumbnail URL"
          value={form.thumbnail}
          onChange={handleChange}
          className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          required
        />

        {form.thumbnail && (
          <div className="flex justify-center">
            <img
              src={form.thumbnail}
              onError={(e) => (e.target.src = "/fallback-thumbnail.jpg")}
              alt="Thumbnail Preview"
              className="w-40 h-auto rounded shadow"
            />
          </div>
        )}

        <select
          name="categoryId"
          value={form.categoryId}
          onChange={handleChange}
          className="border p-2 w-full rounded focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          required
        >
          <option value="">Seleziona Categoria</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>

        <label className="block font-medium mb-1">Seleziona una o più Reti</label>
        <select
          multiple
          name="networkId"
          value={form.networkId || []}
          onChange={handleNetworksChange}
          className="border p-2 w-full rounded h-40 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          required
        >
          {networks.map((net) => (
            <option key={net.id} value={net.id}>
              {net.name}
            </option>
          ))}
        </select>

        {/* Tip + contatore selezionate */}
        <p className="text-xs text-gray-500 mt-1">
          Tip: usa Ctrl/Cmd per selezioni multiple.
        </p>
        <div className="text-xs text-gray-600">
          Selezionate: <b>{selectedCount}</b> / {totalNetworks}
        </div>

        {selectedCount > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {(form.networkId || []).map((id) => {
              const net = networks.find((n) => n.id === Number(id));
              return (
                <span
                  key={id}
                  className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm"
                >
                  {net?.name || `ID ${id}`}
                </span>
              );
            })}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="bg-black text-white py-2 w-full rounded hover:bg-gray-800 disabled:opacity-50"
        >
          {isLoading ? "Uploading..." : "Aggiungi Video"}
        </button>
      </form>
    </div>
  );
};

export default VideoUploader;
